import React, { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Skeleton from 'react-loading-skeleton';
import { useTheme } from 'styled-components';
import { BackArrow, Button, Table, TopProgress } from '../../../../components';
import Dialog from '../../../../components/dialog';
import UniversalModal from '../../../../components/modal-alerts/universal-modal';
import { BodyDefault, HeadLine1 } from '../../../../components/typography/styled';
import { billingService } from '../../../../api';
import { icons } from '../../../../icons';
import { useUi } from '../../../../context/ui.context';
import { usePageTitle } from '../../../../context/navigationContext';
import { formatOrderTotal, getApiErrorMessage, getEntityId } from '../helpers';
import { Container, DetailLabel, DetailRow, DetailValue, Header, Panel, Section, TitleWrapper } from '../styles';

const getOrderStatusFromResponse = (data: any) => {
  return data?.order_status || data?.object?.order_status || data?.result?.order_status;
};

const sameData = (first: any, second: any) => JSON.stringify(first) === JSON.stringify(second);

const OrdersCart: FC = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const theme:any = useTheme();
  const { viewAlert } = useUi();
  usePageTitle(t('SHOPPING_CART'));

  const [cart, setCart] = useState<any>();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [deleteItem, setDeleteItem] = useState<any>();
  const [placeOrderModalOpen, setPlaceOrderModalOpen] = useState(false);

  const getCart = (options: { silent?: boolean } = {}) => {
    if (!options.silent) {
      setLoading(true);
    }
    billingService.getCart()
      .then((res:any) => {
        const data = res.data?.object || res.data;
        setCart((prev:any) => sameData(prev, data) ? prev : data);
      })
      .catch((error:any) => {
        viewAlert({
          severity: 'error',
          message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
        });
      })
      .finally(() => {
        if (!options.silent) {
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    getCart();
  }, []);

  const handleDelete = async () => {
    if (!deleteItem) return;
    setActionLoading(true);
    try {
      await billingService.deleteCartItem(deleteItem.id);
      viewAlert({ severity: 'success', message: t('CART_ITEM_DELETED') });
      setDeleteItem(undefined);
      getCart({ silent: true });
    } catch (error:any) {
      viewAlert({
        severity: 'error',
        message: getApiErrorMessage(error, t('ERROR_OCCURRED')),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handlePlaceOrder = async () => {
    setActionLoading(true);
    try {
      const res = await billingService.placeCartOrder();
      viewAlert({ severity: 'success', message: t('ORDER_PLACED') });
      setPlaceOrderModalOpen(false);
      if (getOrderStatusFromResponse(res.data) === 'fraud') {
        navigate('/orders/under-review');
      } else {
        navigate('/billing/invoices');
      }
    } catch (error:any) {
      viewAlert({
        severity: 'error',
        message: getApiErrorMessage(error, t('FAILED_TO_PLACE_ORDER')),
      });
    } finally {
      setActionLoading(false);
    }
  };

  const cartRows = useMemo(() => {
    return (cart?.items || []).map((item:any) => ({
      ...item,
      product: (
        <div>
          <div>{item.name || 'n/a'}{item.description ? ` - ${item.description}` : ''}</div>
          {item.configurable_options?.map((option:any, index:number) => (
            <BodyDefault key={`${item.id}-option-${index}`} style={{fontSize: '13px', opacity: 0.75}}>
              {option.description || option.display || option.name}
              {option.price !== undefined ? ` (${formatOrderTotal(option.price, cart.currency)})` : ''}
            </BodyDefault>
          ))}
          {Number(item.setup_fees_total) > 0 ? (
            <BodyDefault style={{fontSize: '13px', opacity: 0.75}}>
              {t('SETUP_FEES')}: {formatOrderTotal(item.setup_fees_total, cart.currency)}
            </BodyDefault>
          ) : null}
        </div>
      ),
      cycle: `${formatOrderTotal(item.price_with_options || item.amount || item.fixed_price, cart.currency)} ${item.cycle_display || ''}`,
      dropOptions: [
        {
          title: t('EDIT'),
          id: 'edit',
          icon: icons.Edit,
          divider: false,
          onClick: () => navigate(`/orders/cart/${item.id}`),
          disabled: cart?.item_permissions && cart.item_permissions['cartitems.update'] === false,
        },
        {
          title: t('DELETE'),
          id: 'delete',
          icon: icons.DeleteIcon,
          divider: false,
          onClick: () => setDeleteItem(item),
          disabled: cart?.item_permissions && cart.item_permissions['cartitems.destroy'] === false,
        },
      ],
    }));
  }, [cart, navigate, t]);

  const addServicesPath = useMemo(() => {
    const clientId = getEntityId(cart?.client);
    const userId = getEntityId(cart?.user);
    const query = new URLSearchParams();
    if (clientId) query.set('client', `${clientId}`);
    if (userId) query.set('user', `${userId}`);
    const queryString = query.toString();
    return `/orders/create${queryString ? `?${queryString}` : ''}`;
  }, [cart]);

  const columns = [
    { title: t('PRODUCT'), key: 'product' },
    { title: t('BILLING_CYCLE'), key: 'cycle' },
    { title: '', key: 'props' },
  ];

  if (loading) {
    return (
      <Container>
        <TopProgress loading={loading} />
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width={280}
          height={30}
          style={{marginBottom: '12px'}}
        />
        <Skeleton
          baseColor={theme.palette[300]}
          highlightColor={theme.palette[20]}
          width='100%'
          height={260}
        />
      </Container>
    );
  }

  return (
    <Container>
      <BackArrow fallbackPath='/orders' />
      <Header>
        <TitleWrapper>
          <HeadLine1>{t('CURRENT_CART_DETAILS')}</HeadLine1>
          <BodyDefault>{t('SHOPPING_CART')}</BodyDefault>
        </TitleWrapper>
        <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
          <Button
            title={t('ORDER_ADDITIONAL_SERVICES')}
            variant='stroke'
            size='display'
            onClick={() => navigate(addServicesPath)}
            disabled={cart?.item_permissions && cart.item_permissions['cartitems.create'] === false}
          />
          <Button
            title={t('PLACE_ORDER')}
            variant='primary'
            size='display'
            onClick={() => setPlaceOrderModalOpen(true)}
            disabled={!cartRows.length || (cart?.cart_permissions && cart.cart_permissions['cart.order'] === false)}
          />
        </div>
      </Header>

      <Section>
        {cartRows.length ? (
          <Table columns={columns} rows={cartRows} hideCheckbox hideOther />
        ) : (
          <BodyDefault>{t('NO_PRODUCT_SELECTED_YET')}</BodyDefault>
        )}
      </Section>

      {cartRows.length ? (
        <Panel style={{marginTop: '12px', alignSelf: 'flex-end', minWidth: '320px'}}>
          <DetailRow>
            <DetailLabel>{t('SUBTOTAL')}</DetailLabel>
            <DetailValue>{formatOrderTotal(cart?.subtotal, cart?.currency)}</DetailValue>
          </DetailRow>
          <DetailRow>
            <DetailLabel>{t('SETUP_FEES')}</DetailLabel>
            <DetailValue>{formatOrderTotal(cart?.setup_fees, cart?.currency)}</DetailValue>
          </DetailRow>
          {cart?.taxes?.map((tax:any) => (
            <DetailRow key={tax.tax_name}>
              <DetailLabel>{tax.tax_name}</DetailLabel>
              <DetailValue>{formatOrderTotal(tax.amount, cart?.currency)}</DetailValue>
            </DetailRow>
          ))}
          <DetailRow>
            <DetailLabel>{t('TOTAL')}</DetailLabel>
            <DetailValue>{formatOrderTotal(cart?.total, cart?.currency)}</DetailValue>
          </DetailRow>
        </Panel>
      ) : null}

      <Dialog open={Boolean(deleteItem)} active={Boolean(deleteItem)} noIframe handleClose={() => setDeleteItem(undefined)}>
        <UniversalModal
          closeModal={handleDelete}
          closeModalCancel={() => setDeleteItem(undefined)}
          alertTitle={t('DELETE')}
          alertSubTitle={t('DELETE_CART_ITEM_CONFIRM')}
          loading={actionLoading}
        />
      </Dialog>

      <Dialog open={placeOrderModalOpen} active={placeOrderModalOpen} noIframe handleClose={() => setPlaceOrderModalOpen(false)}>
        <UniversalModal
          closeModal={handlePlaceOrder}
          closeModalCancel={() => setPlaceOrderModalOpen(false)}
          alertTitle={t('PLACE_ORDER')}
          alertSubTitle={t('PLACE_ORDER_CONFIRM')}
          loading={actionLoading}
        />
      </Dialog>
    </Container>
  );
};

export default OrdersCart;
